import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";

type ModalSize = "sm" | "md" | "lg";

export interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    title?: string;
    size?: ModalSize;
    showCloseButton?: boolean;
    // Optional id to link header to dialog for a11y
    ariaLabelledBy?: string;
    // If you prefer an aria-label instead of heading linkage
    ariaLabel?: string;
    // Render into a portal root (defaults to document.body)
    container?: Element | DocumentFragment | null;
    children: React.ReactNode;
    footer?: React.ReactNode;
    className?: string;
    backdropClassName?: string;
    preventScroll?: boolean;
}

const sizeToMaxWidth: Record<ModalSize, string> = {
    sm: "max-w-sm",
    md: "max-w-md",
    lg: "max-w-2xl",
};

export const Modal: React.FC<ModalProps> = ({
                                                isOpen,
                                                onClose,
                                                title,
                                                size = "md",
                                                showCloseButton = true,
                                                ariaLabelledBy,
                                                ariaLabel,
                                                container = typeof document !== "undefined" ? document.body : null,
                                                children,
                                                footer,
                                                className = "",
                                                backdropClassName = "",
                                                preventScroll = true,
                                            }) => {
    const dialogRef = useRef<HTMLDivElement>(null);
    const firstFocusableRef = useRef<HTMLButtonElement>(null);
    const headingId = ariaLabelledBy || (title ? "modal-title-" + Math.random().toString(36).slice(2) : undefined);

    // Prevent background scroll
    useEffect(() => {
        if (!isOpen || !preventScroll) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = prev;
        };
    }, [isOpen, preventScroll]);

    // Escape + focus trap
    useEffect(() => {
        if (!isOpen) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                e.preventDefault();
                onClose();
                return;
            }
            if (e.key === "Tab" && dialogRef.current) {
                const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
                    'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
                );
                if (focusable.length === 0) return;

                const first = focusable[0];
                const last = focusable[focusable.length - 1];

                if (e.shiftKey) {
                    if (document.activeElement === first) {
                        last.focus();
                        e.preventDefault();
                    }
                } else {
                    if (document.activeElement === last) {
                        first.focus();
                        e.preventDefault();
                    }
                }
            }
        };

        document.addEventListener("keydown", handleKeyDown);
        // initial focus
        firstFocusableRef.current?.focus();

        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [isOpen, onClose]);

    if (!isOpen || !container) return null;

    const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) onClose();
    };

    const content = (
        <div
            className={`fixed inset-0 z-100 flex items-center justify-center bg-black/50 backdrop-blur-[1px] ${backdropClassName}`}
            onClick={handleOverlayClick}
            role="dialog"
            aria-modal="true"
            aria-labelledby={headingId}
            aria-label={ariaLabel}
        >
            <div
                ref={dialogRef}
                className={[
                    "relative w-full mx-4 rounded-xl bg-white shadow-2xl p-6 sm:p-7",
                    sizeToMaxWidth[size],
                    "animate-in fade-in zoom-in-95 data-[motion=reduce]:animate-none",
                    className,
                ].join(" ")}
            >
                {showCloseButton && (
                    <button
                        ref={firstFocusableRef}
                        onClick={onClose}
                        aria-label="Close"
                        className="absolute right-4 top-4 inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                    >
                        <span className="text-2xl leading-none">×</span>
                    </button>
                )}

                {title && (
                    <h2 id={headingId} className="text-center text-2xl font-bold text-gray-900">
                        {title}
                    </h2>
                )}

                <div className={title ? "mt-4" : ""}>{children}</div>

                {footer && <div className="mt-6">{footer}</div>}
            </div>
        </div>
    );

    return createPortal(content, container);
};